import { useState, useCallback } from 'react';

interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any) => string | null;
}

interface UseFormState<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isValid: boolean;
  isSubmitting: boolean;
}

interface UseFormReturn<T> extends UseFormState<T> {
  handleChange: (field: keyof T) => (value: any) => void;
  handleBlur: (field: keyof T) => () => void;
  setFieldValue: (field: keyof T, value: any) => void;
  setFieldError: (field: keyof T, error: string) => void;
  validateField: (field: keyof T) => void;
  validateForm: () => boolean;
  resetForm: (values?: Partial<T>) => void;
  setSubmitting: (submitting: boolean) => void;
}

export const useForm = <T extends Record<string, any>>(
  initialValues: T,
  validationRules?: Partial<Record<keyof T, ValidationRule>>
): UseFormReturn<T> => {
  const [state, setState] = useState<UseFormState<T>>({
    values: initialValues,
    errors: {},
    touched: {},
    isValid: false,
    isSubmitting: false,
  });

  const handleChange = useCallback(
    (field: keyof T) => (value: any) => {
      setState(prev => ({
        ...prev,
        values: { ...prev.values, [field]: value },
        errors: { ...prev.errors, [field]: undefined },
      }));
    },
    []
  );

  const handleBlur = useCallback(
    (field: keyof T) => () => {
      setState(prev => ({
        ...prev,
        touched: { ...prev.touched, [field]: true },
      }));
    },
    []
  );

  const setFieldValue = useCallback((field: keyof T, value: any) => {
    setState(prev => ({
      ...prev,
      values: { ...prev.values, [field]: value },
      errors: { ...prev.errors, [field]: undefined },
    }));
  }, []);

  const setFieldError = useCallback((field: keyof T, error: string) => {
    setState(prev => ({
      ...prev,
      errors: { ...prev.errors, [field]: error },
    }));
  }, []);

  const validateField = useCallback(
    (field: keyof T) => {
      if (!validationRules || !validationRules[field]) return;

      const fieldValue = state.values[field];
      const fieldRules = validationRules[field]!;
      
      const validateFieldFn = (value: any, rules: ValidationRule) => {
        if (rules.required && (!value || value.toString().trim() === '')) {
          return 'This field is required';
        }
        if (!value || value.toString().trim() === '') return null;
        
        const stringValue = value.toString();
        
        if (rules.minLength && stringValue.length < rules.minLength) {
          return `Minimum length is ${rules.minLength} characters`;
        }
        
        if (rules.maxLength && stringValue.length > rules.maxLength) {
          return `Maximum length is ${rules.maxLength} characters`;
        }
        
        if (rules.pattern && !rules.pattern.test(stringValue)) {
          return 'Invalid format';
        }
        
        if (rules.custom) {
          return rules.custom(value);
        }
        
        return null;
      };

      const error = validateFieldFn(fieldValue, fieldRules);
      setFieldError(field, error || '');
    },
    [state.values, validationRules, setFieldError]
  );

  const validateForm = useCallback(() => {
    if (!validationRules) return true;

    const errors: Partial<Record<keyof T, string>> = {};
    
    Object.keys(validationRules).forEach((field) => {
      const fieldKey = field as keyof T;
      const fieldValue = state.values[fieldKey];
      const fieldRules = validationRules[fieldKey]!;
      
      if (fieldRules.required && (!fieldValue || fieldValue.toString().trim() === '')) {
        errors[fieldKey] = 'This field is required';
      }
    });

    const isValid = Object.keys(errors).length === 0;

    setState(prev => ({
      ...prev,
      errors,
      isValid,
    }));

    return isValid;
  }, [state.values, validationRules]);

  const resetForm = useCallback((values?: Partial<T>) => {
    setState({
      values: { ...initialValues, ...values },
      errors: {},
      touched: {},
      isValid: false,
      isSubmitting: false,
    });
  }, [initialValues]);

  const setSubmitting = useCallback((submitting: boolean) => {
    setState(prev => ({ ...prev, isSubmitting: submitting }));
  }, []);

  return {
    ...state,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldError,
    validateField,
    validateForm,
    resetForm,
    setSubmitting,
  };
}; 